/**
 * The method library — §11.1, §11.2.
 *
 * THE RESEARCH FINDING THIS IS BUILT ON: every serious hifz curriculum is the same
 * three daily blocks under different names — new (سبق), recent (سبقي), old (دور) —
 * and the methods differ only in THREE NUMBERS: how much is new, how wide the recent
 * window is, and how the old rotation is cut. That is why a preset library is the
 * right shape: a preset is a named tuple of those numbers, not a separate engine.
 */

export type VolumeUnit = 'line' | 'page' | 'ayah' | 'juz'
export type MethodKind = 'hifz' | 'murajaah' | 'tilawah'

export type RecentPolicy =
  { type: 'none' } | { type: 'days'; n: number } | { type: 'pages'; n: number }

export type OldPolicy =
  | { type: 'srs' }
  | { type: 'sections'; n: number }
  | { type: 'window'; back: number; every: number }
  | { type: 'rate'; unit: VolumeUnit; amount: number }

export interface MethodVolume {
  unit: VolumeUnit
  amount: number
  /** The daily review ceiling — the most pages of murājaʿah the plan will ask for. */
  review: number
}

/**
 * I3: `name`, `blurb` and `source` were user-facing Arabic and moved to
 * `apps/web/src/content/methods.ts`. What is left is the method itself — the numbers.
 */
export interface HifzMethod {
  id: string
  kind: MethodKind
  defaultVolume: MethodVolume
  recent: RecentPolicy
  old: OldPolicy
  /**
   * Overdue pages that withhold سبق; 0 disables.
   *
   * NEW MEMORISATION PAUSES ITSELF. Every source lists "kept adding new while review
   * slipped" as the primary cause of decay, so this is a feature, not polish.
   */
  pauseNewAt: number
}

export const HIFZ_METHODS: readonly HifzMethod[] = [
  {
    id: 'classical',
    kind: 'hifz',
    defaultVolume: { unit: 'line', amount: 8, review: 10 },
    recent: { type: 'days', n: 25 },
    old: { type: 'sections', n: 7 },
    pauseNewAt: 15,
  },
  {
    id: 'qarib-baid',
    kind: 'hifz',
    defaultVolume: { unit: 'page', amount: 1, review: 20 },
    recent: { type: 'pages', n: 20 },
    old: { type: 'window', back: 20, every: 7 },
    pauseNewAt: 20,
  },
  {
    id: 'juz-daily',
    kind: 'murajaah',
    defaultVolume: { unit: 'juz', amount: 1, review: 21 },
    recent: { type: 'none' },
    old: { type: 'rate', unit: 'juz', amount: 1 },
    pauseNewAt: 0,
  },
  {
    // Named generically on purpose; the attribution note lives with the name, in
    // `apps/web/src/content/methods.ts`.
    id: 'hadr-15',
    kind: 'murajaah',
    defaultVolume: { unit: 'juz', amount: 2, review: 42 },
    recent: { type: 'none' },
    old: { type: 'rate', unit: 'juz', amount: 2 },
    pauseNewAt: 0,
  },
  {
    id: 'muyassar',
    kind: 'hifz',
    defaultVolume: { unit: 'ayah', amount: 4, review: 5 },
    recent: { type: 'days', n: 7 },
    old: { type: 'sections', n: 7 },
    pauseNewAt: 8,
  },
  {
    id: 'tilawah',
    kind: 'tilawah',
    defaultVolume: { unit: 'page', amount: 4, review: 0 },
    recent: { type: 'none' },
    old: { type: 'srs' },
    pauseNewAt: 0,
  },
  {
    id: 'custom',
    kind: 'hifz',
    defaultVolume: { unit: 'page', amount: 1, review: 10 },
    recent: { type: 'pages', n: 10 },
    old: { type: 'srs' },
    pauseNewAt: 0,
  },
]

export const methodById = (id: string): HifzMethod | undefined =>
  HIFZ_METHODS.find((m) => m.id === id)
